import CustomField, { EntityCustomField } from 'App/resources/Model/CustomField';
import { getCFFieldIdFromFilterName, getNameTypeFromColumn } from 'App/helpers/filterHelper';
import TelephoneLink from 'App/components/columns/TelephoneLink';
import { Link, Text } from '@upsales/components';
import T from 'Components/Helpers/translate';
import { useSelector } from 'react-redux';
import { RootState } from 'Store/index';
import React, { useMemo } from 'react';
import moment from 'moment';
import { parseCustomFieldUserValues } from 'Services/ActionProperties';

type CustomFieldValueTextFieldsProps = {
	type?: keyof RootState['App']['customFields'];
	fields: CustomField[];
	column: string;
	values: EntityCustomField[];
	size?: React.ComponentProps<typeof Text>['size'];
};
type CustomFieldValueTextProps = {
	type: keyof RootState['App']['customFields'];
	fields?: CustomField[];
	column: string;
	values: EntityCustomField[];
	size?: React.ComponentProps<typeof Text>['size'];
};

export function getCorrectCustomFieldValue(value: string | null, datatype?: CustomField['datatype']) {
	let content: string | JSX.Element | null = value;
	if (!datatype) return content;

	if (datatype === 'Boolean') {
		content = value ? T('default.yes') : T('default.no');
	} else if (datatype === 'Date' && value) {
		const m = moment(value);
		if (m.isValid()) {
			content = m.format('L');
		} else {
			content = null;
		}
	} else if (datatype === 'Link' && value) {
		content = (
			<Link target="_blank" href={value} onClick={e => e.stopPropagation()}>
				{value}
			</Link>
		);
	} else if (datatype === 'Phone' && value?.trim()) {
		content = <TelephoneLink number={value} />;
	} else if (['User', 'Users'].includes(datatype) && value) {
		content = parseCustomFieldUserValues(value);
	}
	return content;
}

const CustomFieldValueText: React.FC<CustomFieldValueTextFieldsProps | CustomFieldValueTextProps> = ({
	values,
	type,
	column,
	size,
	fields: providedFields
}) => {
	const selectedFields = useSelector(({ App }: RootState) => (type ? App.customFields[type] : []));
	const fields: CustomField[] = type ? selectedFields : providedFields || [];
	const fieldId = getCFFieldIdFromFilterName(column);
	const nameType = getNameTypeFromColumn(column);
	const field = useMemo(() => {
		const cf = fields.find(cf => {
			return cf.id === fieldId && cf.nameType === nameType;
		});

		return cf;
	}, [fields, fieldId, nameType]);
	// Within a single entity's `custom` array, fieldId uniquely identifies the field. The entity
	// value objects never carry `nameType` (only the field definitions do), so matching on it here
	// would always fail for nameType-tagged columns like CustomAppointment_<id> (WEB-7178).
	const valueObj = useMemo(() => values.find(v => v.fieldId === fieldId), [values, fieldId]);

	if (!field) {
		return null;
	}

	const value = valueObj?.value ?? null;
	const content: JSX.Element | string | null = getCorrectCustomFieldValue(value, field?.datatype);

	return content ? <Text size={size}>{content}</Text> : null;
};

export default CustomFieldValueText;
